import { useMemo } from 'react';
import { useLocale, useTranslate } from '../../application/i18n/LocaleProvider';
import { useCarCatalogue } from '../../application/cars/useCarCatalogue';
import { useBrowseFilters, useRememberedScroll } from '../../application/cars/browseState';
import useCarCollection from '../../application/game/useCarCollection';
import { sortCars } from '../../domain/cars/carCatalogue';
import {
    NO_FILTERS,
    filterCars,
    listFacets,
    toggleFacetValue,
} from '../../domain/cars/carFilters';
import CarCard from '../cars/CarCard';
import CarLockedCard from '../cars/CarLockedCard';
import GarageQuickFilter from '../cars/GarageQuickFilter';
import { WheelIcon } from '../icons/ControlIcons';
import AppOffers from '../pwa/AppOffers';

/**
 * The catalogue (FR-03) — a gallery of twelve cars, one column on a phone.
 *
 * The screen is a composition and almost nothing else: the catalogue arrives
 * from a hook, the shelf is `filterCars` over it, the facets belong to the
 * over the same list, and the selection is the URL. There is no state declared
 * in this file at all, which is the point — a garage link opens the shelf it was
 * sent as, and the back button out of a car lands on the shelf the reader left.
 *
 * The ORDER is the domain's (`sortCars`: by year, then by name), not a control.
 * A sort picker was on the table and the message catalogue still carries the
 * copy for one; it is out because the product is about an ERA and the shelf
 * reading as a walk from 1986 to 1994 is the story it is telling. Sorting by top
 * speed would turn a gallery into a league table.
 *
 * Every state on this screen is a designed one. A loading garage says so, a
 * failed fetch says what to do about it, and a filter that matches nothing gets
 * a real empty state with the way out in it — never a blank screen, which on a
 * black page is indistinguishable from a broken one.
 *
 * SINCE SPEC-010 THERE ARE TWO SHELVES. The first is the reader's own cars,
 * drawn exactly as they always were. The second is everything still to be won,
 * as silhouettes — and it is a band of its own under the first rather than
 * locked cards mixed into the grid, because a blacked-out card between two
 * photographs reads as an image that failed to load. A heading over it is what
 * turns twenty-four cards minus twelve into twelve cards and a reason to play.
 *
 * The FILTERS reach both shelves and the FACETS are read off the whole
 * catalogue. Filtering to JDM icons and being shown that two of them are still
 * to be won is the lock doing its job; a facet that disappeared because its only
 * car was locked would be the catalogue hiding from its own reader.
 */
export default function GarageScreen() {
    const t = useTranslate();
    // The shelf's order has to be the DECK's order — `useCarDeck` sorts the
    // same list with the language on screen, and a garage sorted against a
    // different one would deal a card the shelf did not promise.
    const { languageCode } = useLocale();
    const { cars, status } = useCarCatalogue();
    const { locked, unlocked } = useCarCollection({ cars });
    const { filters, setFilters, search } = useBrowseFilters();
    const clearFilters = () => setFilters(NO_FILTERS);

    // Sorted once per catalogue, then filtered. The other way round would re-sort
    // twelve records on every chip tap for an order that cannot have changed.
    const shelf = useMemo(() => sortCars(unlocked, languageCode), [unlocked, languageCode]);
    const visible = useMemo(() => filterCars(shelf, filters), [shelf, filters]);
    // Off the WHOLE catalogue, not off either shelf and not off `visible` — a
    // facet's options must not vanish as the reader narrows with them, or
    // turning one off becomes impossible, and they must not vanish because the
    // only car carrying one has not been won yet.
    const facets = useMemo(() => listFacets(sortCars(cars, languageCode)), [cars, languageCode]);
    // In the CATALOGUE's order rather than the shelf's, because this band is a
    // ladder: the first card in it is the next one the competition pays out.
    const stillToWin = useMemo(() => filterCars(locked, filters), [locked, filters]);

    // Only once there is something to scroll. Restoring against a loading screen
    // sets a scroll position on an empty container, and the browser keeps it.
    useRememberedScroll(status === 'ready');

    return (
        <section className="garage">
            {/* The composition goes straight from the lockup to the cars — a
                title would cost a third of the fold and say nothing the wordmark
                above it does not. One facet row survives above the grid and the
                rest are behind the header's own control, so the fold belongs to
                the artwork. The heading stays in the document because a screen
                still needs one to be announced and navigated by; only its pixels
                are gone. */}
            <div className="visually-hidden">
                <h1>{t('garage.title')}</h1>
                <p>{t('garage.intro')}</p>
            </div>

            {status === 'ready' ? (
                <>
                    <GarageQuickFilter
                        facets={facets}
                        filters={filters}
                        onToggle={(facetKey, value) => setFilters(toggleFacetValue(filters, facetKey, value))}
                    />
                    {/* A live region, because the count is the only feedback a
                        chip tap gives somebody who is not looking at the grid. */}
                    <p className="garage__count" role="status">
                        {visible.length === 1 ? t('garage.countOne') : t('garage.count', { count: visible.length })}
                    </p>
                </>
            ) : null}

            {status === 'loading' ? <p className="garage__status">{t('garage.loading')}</p> : null}

            {status === 'failed' ? (
                <div className="empty-state">
                    <WheelIcon className="empty-state__mark" size={32} />
                    <p className="empty-state__message">{t('garage.failed')}</p>
                </div>
            ) : null}

            {/* Empty only where BOTH shelves are. A filter that matches nothing
                the reader owns but does match something still to be won is not
                an empty result — it is the answer "you have not won that one
                yet", and the band underneath is already giving it. */}
            {status === 'ready' && visible.length === 0 && stillToWin.length === 0 ? (
                <div className="empty-state">
                    <WheelIcon className="empty-state__mark" size={32} />
                    <p className="empty-state__message">{t('garage.empty')}</p>
                    <button type="button" className="button button--quiet" onClick={clearFilters}>
                        {t('garage.emptyAction')}
                    </button>
                </div>
            ) : null}

            {visible.length > 0 ? (
                <div className="garage__grid">
                    {visible.map((car, position) => (
                        <CarCard key={car.id} car={car} search={search} isFirst={position === 0} />
                    ))}
                </div>
            ) : null}

            {/* Under the open shelf and above the install offer, which is the
                order of what the reader owns, what they could own, and what the
                app would like from them. */}
            {status === 'ready' && stillToWin.length > 0 ? (
                <section className="garage__locked" aria-labelledby="garage-locked-title">
                    <header className="section-heading">
                        <h2 className="section-heading__text" id="garage-locked-title">
                            {t('collection.title')}
                        </h2>
                        <span className="section-heading__rule" aria-hidden="true" />
                    </header>

                    <p className="garage__locked-intro">{t('collection.intro')}</p>
                    <p className="garage__locked-count">
                        {stillToWin.length === 1
                            ? t('collection.countOne')
                            : t('collection.count', { count: stillToWin.length })}
                    </p>

                    <div className="garage__locked-grid">
                        {stillToWin.map((car) => <CarLockedCard key={car.id} car={car} />)}
                    </div>
                </section>
            ) : null}

            {/* The garage is complete, said once and quietly. It is the end of
                the ladder and there is nothing to sell at it — a reader who has
                won every car does not need a band telling them so twice. */}
            {status === 'ready' && locked.length === 0 && cars.length > 0 ? (
                <p className="garage__complete">{t('collection.complete')}</p>
            ) : null}

            {/* At the FOOT of the garage and inside the scroll, on this screen
                only: it is the one place in the app that may ask the reader for
                anything, and asking from the bottom of a page they have already
                read is the difference between an offer and an interruption. */}
            <AppOffers />
        </section>
    );
}
